import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiPlus, 
  FiEdit, 
  FiTrash2, 
  FiEye, 
  FiEyeOff,
  FiSearch, 
  FiFilter,
  FiCalendar,
  FiUser,
  FiTag,
  FiHeart,
  FiPlay,
  FiFileText,
  FiStar,
  FiMoreHorizontal
} from 'react-icons/fi';
import { FaVideo, FaNewspaper } from 'react-icons/fa';
import { formatDate, formatNumber } from '../../utils/helpers';
import { getBlogs, getBlogStats, deleteBlog, updateBlog } from '../../utils/adminApi';

const AdminBlogs = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedType, setSelectedType] = useState('All');
  const [selectedStatus, setSelectedStatus] = useState('All');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [blogToDelete, setBlogToDelete] = useState(null);
  const [stats, setStats] = useState({});
  const [pagination, setPagination] = useState({
    page: 1,
    pages: 1,
    total: 0,
    per_page: 10
  });

  useEffect(() => {
    fetchBlogs();
    fetchStats();
  }, [pagination.page, searchTerm, selectedCategory, selectedType, selectedStatus]);

  const fetchBlogs = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        per_page: pagination.per_page,
        ...(searchTerm && { search: searchTerm }),
        ...(selectedCategory !== 'All' && { category: selectedCategory }),
        ...(selectedType !== 'All' && { type: selectedType }),
        ...(selectedStatus !== 'All' && { published: selectedStatus === 'Published' })
      };

      const data = await getBlogs(params);
      setBlogs(data.blogs || []);
      setPagination(prev => ({ ...prev, ...(data.pagination || {}) }));
    } catch (error) {
              console.error('Error fetching blogs:', error);
      setBlogs([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const data = await getBlogStats();
      setStats(data.stats || data);
    } catch (error) {
              console.error('Error fetching stats:', error);
    }
  };

  const handleDelete = async (blogId) => {
    try {
      await deleteBlog(blogId);
      setBlogs(blogs.filter(blog => blog.id !== blogId));
      setShowDeleteModal(false);
      setBlogToDelete(null);
      await fetchStats(); // Refresh stats
    } catch (error) {
              console.error('Error deleting blog:', error);
    }
  };

  const toggleFeatured = async (blogId, currentFeatured) => {
    try {
      await updateBlog(blogId, { featured: !currentFeatured });
      setBlogs(blogs.map(blog => 
        blog.id === blogId 
          ? { ...blog, featured: !currentFeatured }
          : blog
      ));
    } catch (error) {
              console.error('Error updating featured status:', error);
    }
  };

  const togglePublished = async (blogId, currentPublished) => {
    try {
      console.log(`Toggling publish status for blog ${blogId}: ${currentPublished} -> ${!currentPublished}`);
      const result = await updateBlog(blogId, { published: !currentPublished });
      console.log('Update result:', result);
      
      setBlogs(blogs.map(blog => 
        blog.id === blogId 
          ? { ...blog, published: !currentPublished }
          : blog
      ));
      
      // Show success message
      alert(`Blog ${!currentPublished ? 'published' : 'unpublished'} successfully!`);
    } catch (error) {
      console.error('Error updating published status:', error);
      alert(`Error ${!currentPublished ? 'publishing' : 'unpublishing'} blog: ${error.message || 'Unknown error'}`);
    }
  };

  const initializeSampleData = async () => {
    if (!confirm('This will add sample blog posts and vlogs. Are you sure you want to continue?')) {
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      const response = await fetch('/api/admin/blogs/init-sample', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        alert(`Successfully created ${data.blogs?.length || 0} sample posts!`);
        await fetchBlogs();
        await fetchStats();
      } else {
        const errorData = await response.json();
        alert(`Failed to create sample data: ${errorData.message}`);
      }
    } catch (error) {
      console.error('Error initializing sample data:', error);
      alert('Error creating sample data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const categories = ['All', 'Nutrition', 'Disease Management', 'Lifestyle', 'Meal Prep', 'Recipes'];
  const types = ['All', 'blog', 'vlog'];
  const statuses = ['All', 'Published', 'Draft'];

  const getStatusColor = (published) => {
    return published 
      ? 'bg-green-100 text-green-800' 
      : 'bg-yellow-100 text-yellow-800';
  };

  const getTypeIcon = (type) => {
    return type === 'vlog' ? <FaVideo className="w-4 h-4" /> : <FaNewspaper className="w-4 h-4" />;
  };

  const getTypeColor = (type) => {
    return type === 'vlog' 
      ? 'bg-red-100 text-red-800' 
      : 'bg-blue-100 text-blue-800';
  };

  if (loading && blogs.length === 0) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">Blog Management</h1>
          <p className="text-neutral-600 mt-1">Create, edit, and manage your blog posts and vlogs</p>
        </div>
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => window.location.href = '/admin/blogs/new'}
          className="bg-primary-500 text-white px-6 py-3 rounded-lg hover:bg-primary-600 transition-colors flex items-center gap-2 font-medium"
        >
          <FiPlus className="w-5 h-5" />
          Create New Post
        </motion.button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white p-6 rounded-xl shadow-sm border border-neutral-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-neutral-600">Total Posts</p>
              <p className="text-2xl font-bold text-neutral-900">{stats.total_blogs || 0}</p>
            </div>
            <div className="bg-blue-100 p-3 rounded-lg">
              <FiFileText className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border border-neutral-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-neutral-600">Published</p>
              <p className="text-2xl font-bold text-green-600">{stats.published_blogs || 0}</p>
            </div>
            <div className="bg-green-100 p-3 rounded-lg">
              <FiEye className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border border-neutral-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-neutral-600">Total Views</p>
              <p className="text-2xl font-bold text-purple-600">{formatNumber(stats.total_views || 0)}</p>
            </div>
            <div className="bg-purple-100 p-3 rounded-lg">
              <FiEye className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border border-neutral-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-neutral-600">Total Likes</p>
              <p className="text-2xl font-bold text-red-600">{formatNumber(stats.total_likes || 0)}</p>
            </div>
            <div className="bg-red-100 p-3 rounded-lg">
              <FiHeart className="w-6 h-6 text-red-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-6 rounded-xl shadow-sm border border-neutral-200">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div className="relative">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search posts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-neutral-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          {/* Category Filter */}
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="px-4 py-2 border border-neutral-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            {categories.map(category => (
              <option key={category} value={category}>
                {category === 'All' ? 'All Categories' : category}
              </option>
            ))}
          </select>

          {/* Type Filter */}
          <select
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
            className="px-4 py-2 border border-neutral-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            {types.map(type => (
              <option key={type} value={type}>
                {type === 'All' ? 'All Types' : type === 'vlog' ? 'Vlogs' : 'Blogs'}
              </option>
            ))}
          </select>

          {/* Status Filter */}
          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            className="px-4 py-2 border border-neutral-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            {statuses.map(status => (
              <option key={status} value={status}>{status}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Blog List */}
      <div className="bg-white rounded-xl shadow-sm border border-neutral-200 overflow-hidden">
        {blogs.length === 0 ? (
          <div className="p-12 text-center">
            <FiFileText className="w-16 h-16 text-neutral-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-neutral-900 mb-2">No posts found</h3>
            <p className="text-neutral-600 mb-6">Create your first blog post or vlog to get started.</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => window.location.href = '/admin/blogs/new'}
                className="bg-primary-500 text-white px-6 py-3 rounded-lg hover:bg-primary-600 transition-colors"
              >
                Create Your First Post
              </button>
              <button
                onClick={initializeSampleData}
                className="bg-green-500 text-white px-6 py-3 rounded-lg transition-colors hover:bg-green-600"
              >
                Add Sample Data
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-neutral-50 border-b border-neutral-200">
                  <tr>
                    <th className="text-left py-3 px-6 font-medium text-neutral-700">Post</th>
                    <th className="text-left py-3 px-6 font-medium text-neutral-700">Type</th>
                    <th className="text-left py-3 px-6 font-medium text-neutral-700">Category</th>
                    <th className="text-left py-3 px-6 font-medium text-neutral-700">Status</th>
                    <th className="text-left py-3 px-6 font-medium text-neutral-700">Stats</th>
                    <th className="text-left py-3 px-6 font-medium text-neutral-700">Date</th>
                    <th className="text-left py-3 px-6 font-medium text-neutral-700 sticky right-0 bg-neutral-50 shadow-[-4px_0_15px_-3px_rgba(0,0,0,0.05)] z-10">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-200">
                  {blogs.map((blog) => (
                    <motion.tr
                      key={blog.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="group hover:bg-neutral-50 transition-colors"
                    >
                      <td className="py-4 px-6">
                        <div className="flex items-start gap-3">
                          <div className="w-16 h-12 bg-neutral-200 rounded-lg flex items-center justify-center overflow-hidden">
                            {blog.type === 'vlog' ? (
                              blog.thumbnail ? (
                                <img src={blog.thumbnail} alt="" className="w-full h-full object-cover" />
                              ) : (
                                <FaVideo className="w-6 h-6 text-neutral-500" />
                              )
                            ) : (
                              blog.image ? (
                                <img src={blog.image} alt="" className="w-full h-full object-cover" />
                              ) : (
                                <FaNewspaper className="w-6 h-6 text-neutral-500" />
                              )
                            )}
                          </div>
                          <div className="flex-1 min-w-0 max-w-sm md:max-w-md">
                            <h3 className="font-medium text-neutral-900 truncate">{blog.title}</h3>
                            <p className="text-sm text-neutral-600 mt-1 line-clamp-2">{blog.excerpt}</p>
                            <div className="flex items-center gap-2 mt-2">
                              {blog.featured && (
                                <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full">
                                  <FiStar className="w-3 h-3" />
                                  Featured
                                </span>
                              )}
                              {blog.tags && blog.tags.slice(0, 2).map((tag, index) => (
                                <span key={index} className="px-2 py-1 bg-neutral-100 text-neutral-600 text-xs rounded-full">
                                  {tag}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${getTypeColor(blog.type)}`}>
                          {getTypeIcon(blog.type)}
                          {blog.type === 'vlog' ? 'Vlog' : 'Blog'}
                        </span>
                      </td>
                      <td className="py-4 px-6">
                        <span className="text-sm text-neutral-700">{blog.category}</span>
                      </td>
                      <td className="py-4 px-6">
                        <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(blog.published)}`}>
                          {blog.published ? 'Published' : 'Draft'}
                        </span>
                      </td>
                      <td className="py-4 px-6">
                        <div className="text-sm text-neutral-600">
                          <div className="flex items-center gap-3">
                            <span className="flex items-center gap-1">
                              <FiEye className="w-4 h-4" />
                              {formatNumber(blog.views)}
                            </span>
                            <span className="flex items-center gap-1">
                              <FiHeart className="w-4 h-4" />
                              {formatNumber(blog.likes)}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <div className="text-sm text-neutral-600">
                          <div>{formatDate(blog.publish_date)}</div>
                          <div className="text-xs text-neutral-500">
                            by {blog.author}
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-6 sticky right-0 bg-white shadow-[-4px_0_15px_-3px_rgba(0,0,0,0.05)] z-10 group-hover:bg-neutral-50 transition-colors">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => window.open(`/blog/${blog.id}`, '_blank')}
                            className="p-2 text-neutral-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="View Post"
                          >
                            <FiEye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => window.location.href = `/admin/blogs/${blog.id}/edit`}
                            className="p-2 text-neutral-600 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                            title="Edit Post"
                          >
                            <FiEdit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => togglePublished(blog.id, blog.published)}
                            className={`p-2 rounded-lg transition-colors ${
                              blog.published 
                                ? 'text-green-600 bg-green-50 hover:bg-green-100' 
                                : 'text-neutral-600 hover:text-green-600 hover:bg-green-50'
                            }`}
                            title={blog.published ? 'Unpublish Post' : 'Publish Post'}
                          >
                            {blog.published ? <FiEyeOff className="w-4 h-4" /> : <FiEye className="w-4 h-4" />}
                          </button>
                          <button
                            onClick={() => toggleFeatured(blog.id, blog.featured)}
                            className={`p-2 rounded-lg transition-colors ${
                              blog.featured 
                                ? 'text-yellow-600 bg-yellow-50 hover:bg-yellow-100' 
                                : 'text-neutral-600 hover:text-yellow-600 hover:bg-yellow-50'
                            }`}
                            title={blog.featured ? 'Remove from Featured' : 'Mark as Featured'}
                          >
                            <FiStar className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => {
                              setBlogToDelete(blog);
                              setShowDeleteModal(true);
                            }}
                            className="p-2 text-neutral-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete Post"
                          >
                            <FiTrash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="p-6 border-t border-neutral-200">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-neutral-600">
                    Showing {((pagination.page - 1) * pagination.per_page) + 1} to {Math.min(pagination.page * pagination.per_page, pagination.total)} of {pagination.total} posts
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                      disabled={pagination.page === 1}
                      className="px-3 py-1 text-sm border border-neutral-200 rounded hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    
                    {[...Array(pagination.pages)].map((_, i) => (
                      <button
                        key={i + 1}
                        onClick={() => setPagination(prev => ({ ...prev, page: i + 1 }))}
                        className={`px-3 py-1 text-sm border rounded ${
                          pagination.page === i + 1
                            ? 'bg-primary-500 text-white border-primary-500'
                            : 'border-neutral-200 hover:bg-neutral-50'
                        }`}
                      >
                        {i + 1}
                      </button>
                    ))}
                    
                    <button
                      onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                      disabled={pagination.page === pagination.pages}
                      className="px-3 py-1 text-sm border border-neutral-200 rounded hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {showDeleteModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl p-6 max-w-md w-full"
            >
              <h3 className="text-lg font-semibold text-neutral-900 mb-2">Delete Post</h3>
              <p className="text-neutral-600 mb-6">
                Are you sure you want to delete "{blogToDelete?.title}"? This action cannot be undone.
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setBlogToDelete(null);
                  }}
                  className="px-4 py-2 text-neutral-600 hover:text-neutral-800 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDelete(blogToDelete.id)}
                  className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                >
                  Delete
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminBlogs;